import React from 'react';
import { ArrowLeft, Truck, Zap, Package, AlertCircle } from 'lucide-react';

const ShippingPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Shipping & Delivery Policy</h1>
                <p className="text-gray-600 mt-2">Effective Date: January 1, 2025</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              Shortlistly is a digital platform, and all of our services — including resume optimization, 
              job targeting, and report generation — are delivered electronically.
            </p>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Digital Services */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <Zap className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Digital Services</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">Instant Delivery</h3>
                <div className="bg-blue-50 p-6 rounded-xl">
                  <div className="flex items-center mb-3">
                    <Zap className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-gray-900">Delivery Timeline</span>
                  </div>
                  <p className="text-gray-700 mb-3">
                    Optimized resumes, reports, or insights are usually delivered within:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li><strong>Resume Optimization:</strong> 30 seconds - 2 minutes</li>
                    <li><strong>Template Generation:</strong> Instant preview</li>
                    <li><strong>Reports & Analytics:</strong> 10-30 seconds</li>
                    <li><strong>ATS Score Calculation:</strong> Real-time</li>
                  </ul>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">Access Methods</h3>
                <div className="bg-green-50 p-6 rounded-xl">
                  <div className="flex items-center mb-3">
                    <Package className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-gray-900">How to Access</span>
                  </div>
                  <p className="text-gray-700 mb-3">
                    All results will be available through:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Your personal dashboard</li>
                    <li>Direct download links</li>
                    <li>Email notifications (optional)</li>
                    <li>Browser download manager</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="font-medium text-gray-900">Important Note</span>
              </div>
              <p className="text-gray-700 mt-2">
                Please ensure your email address is correct to receive download links and notifications. 
                Check your spam folder if you don't receive emails within 5 minutes.
              </p>
            </div>
          </div>

          {/* Physical Products (Future) */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <Package className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Physical Products (Future Plans)</h2>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-xl mb-6">
              <p className="text-gray-700 mb-4">
                In the future, if we offer any physical deliverables such as printed resumes, 
                career guidance materials, or professional portfolio kits:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Shipping Regions</h3>
                <p className="text-gray-600 text-sm">Currently planning to ship only within India unless stated otherwise</p>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Delivery Time</h3>
                <p className="text-gray-600 text-sm">5–10 business days depending on your location within India</p>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Shipping Charges</h3>
                <p className="text-gray-600 text-sm">Fees will be clearly mentioned during checkout process</p>
              </div>
            </div>
          </div>

          {/* Delivery Issues */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Non-Receipt of Digital Deliverables</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              If you don't receive your optimized resume, report, or any digital deliverable, please follow these steps:
            </p>

            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Check Your Email</h3>
                  <p className="text-gray-600">Look in your spam, promotions, or junk folder for delivery notifications</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Check Your Dashboard</h3>
                  <p className="text-gray-600">Ensure your dashboard doesn't show any processing errors or failed deliveries</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Contact Support</h3>
                  <p className="text-gray-600">
                    Still stuck? Email us at <a href="mailto:shortlistly@gmail.com" className="text-purple-600 hover:text-purple-700">shortlistly@gmail.com</a> for help within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Download Tips */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Download & Access Tips</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Best Practices</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Use a reliable internet connection for downloads</li>
                  <li>Ensure sufficient storage space on your device</li>
                  <li>Save important files to cloud storage as backup</li>
                  <li>Keep your browser updated for best compatibility</li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">File Formats Available</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li><strong>PDF:</strong> Professional formatted resumes</li>
                  <li><strong>DOCX:</strong> Editable Microsoft Word format</li>
                  <li><strong>TXT:</strong> Plain text for easy editing</li>
                  <li><strong>HTML:</strong> Web-ready format</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Delivery Questions?</h2>
            <p className="text-blue-100 mb-6">
              Having trouble accessing your optimized resume or need help with downloads? We're here to assist!
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="mailto:shortlistly@gmail.com"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Get Help Now
              </a>
              <span className="text-blue-100 flex items-center">
                📧 Response within 24 hours guaranteed
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;